// Loan dashboard — needs-attention feed (the Due Dates tab's successor).
// Union of due/overdue items across every discipline, sorted worst-severity
// then date (server-side), capped at 8 with "View all (n)". Calendar-backed
// rows open the existing per-item drawer (complete / snooze); derived rows
// deep-link to their tab.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Skeleton, Truncate } from "@/design-system/components/ui";
import { useLoanDueDates } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type {
  LoanAttentionItemDto,
  LoanHealthDto,
} from "@/api/portfolioAdmin/loanHealthApi";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";
import { DueDateItemDrawer } from "../dueDates/DueDateItemDrawer";
import { AttentionFeedDrawer } from "./AttentionFeedDrawer";
import { daysLabel, daysUntil } from "../common/colorBand";
import { SEVERITY_DOT_CLASS } from "./healthDisplay";

const FEED_CAP = 8;

interface NeedsAttentionCardProps {
  loanId: string;
  health: LoanHealthDto | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function NeedsAttentionCard({
  loanId,
  health,
  isLoading,
  isError,
}: NeedsAttentionCardProps) {
  const navigate = useNavigate();
  // Full calendar DTOs hydrate the per-item drawer for calendar-backed
  // rows. Cached list; a 403 (no Compliance.Calendar grant) just degrades
  // those rows to tab deep-links.
  const dueDates = useLoanDueDates(loanId);
  const [openItem, setOpenItem] = useState<ComplianceCalendarItemDto | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const items = useMemo(() => health?.attention ?? [], [health]);
  const visible = items.slice(0, FEED_CAP);

  const onRowClick = (row: LoanAttentionItemDto) => {
    if (row.calendarItemId) {
      const dto = (dueDates.data ?? []).find((i) => i.id === row.calendarItemId);
      if (dto) {
        setOpenItem(dto);
        return;
      }
    }
    navigate(`/portfolio-admin/loans/${loanId}?tab=${row.targetTab}`);
  };

  return (
    <Card data-testid="needs-attention-card">
      <div className="flex items-baseline justify-between mb-space-2">
        <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
          Needs attention
        </h3>
        {items.length > 0 && (
          <button
            type="button"
            className="text-caption text-gold-shadow hover:underline"
            onClick={() => setViewAllOpen(true)}
            data-testid="needs-attention-view-all"
          >
            View all ({items.length})
          </button>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-space-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-small text-status-error m-0">
          Could not load the attention feed. Refresh to retry.
        </p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-small text-gray-mid m-0" data-testid="needs-attention-empty">
          Nothing needs attention.
        </p>
      )}

      {!isLoading && !isError && visible.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col" data-testid="needs-attention-list">
          {visible.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => onRowClick(row)}
                className="w-full flex flex-col items-start gap-[2px] py-space-1 px-space-1 rounded-sm text-left hover:bg-white-warm"
                data-testid={`attention-row-${row.id}`}
              >
                {/* 🔴 THE TITLE GETS THE WHOLE LINE, AND THAT IS THE POINT.
                    One-line rows truncated the only part worth scanning: at the
                    rail's correct 352px the title had ~170px left after the
                    days figure and the badge, against titles measured at
                    342.45px — "Annual good-standing pu…", which is a rumour of
                    a work item rather than one. The figure and the badge are
                    short and fixed, so they lose nothing by moving down. */}
                <span className="flex items-start gap-space-2 w-full min-w-0">
                  <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full shrink-0 mt-[6px] ${SEVERITY_DOT_CLASS[row.severity]}`}
                  />
                  {/* Two lines, then an ellipsis (§11.2) — bounded, so a
                      pathological title cannot make one row six lines tall —
                      and revealed through the PORTALED Tooltip rather than the
                      native `title` this replaced. `title` never opens on
                      keyboard focus, which §11.4 forbids for exactly that
                      reason. The reveal is measured, so a short title is not a
                      tab stop. */}
                  <Truncate
                    lines={2}
                    revealOnlyWhenClamped
                    className="text-small text-onyx break-words flex-1"
                  >
                    {row.title}
                  </Truncate>
                </span>
                <span className="flex items-center gap-space-2 pl-[16px]">
                  {row.dueDate && (
                    <span className="text-caption text-gray-mid tabular-nums">
                      {daysLabel(daysUntil(row.dueDate))}
                    </span>
                  )}
                  <span className="text-caption text-gray-mid border border-border-light rounded-full px-space-1">
                    {row.ownerRole}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {openItem && (
        <DueDateItemDrawer
          loanId={loanId}
          item={openItem}
          open={!!openItem}
          onClose={() => setOpenItem(null)}
        />
      )}

      {viewAllOpen && (
        <AttentionFeedDrawer
          loanId={loanId}
          items={items}
          open={viewAllOpen}
          onClose={() => setViewAllOpen(false)}
        />
      )}
    </Card>
  );
}
